// ConnectionsPanel.tsx — ONE place for everything outside himself.
// His public address (the one fact Telegram, Google and connector sign-ins
// share), where he reaches you (Telegram), his accounts (Google), outside
// services (MCP connectors), and the API keys he runs on. Every row is a card
// with a status dot; open it for setup, a how-to, and the controls. The API-key
// services he already ran on (OpenAI, Spotify, Twitter/X, Vectorizer.AI, ...) are
// the last section, always visible: connectors add to them, never replace them.
// No separate tabs, and nothing that has to be typed into an .env file.
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleAuth } from '../hooks/useGoogleAuth';
import CredentialSettings from './CredentialSettings';
import GoogleIntegrationSettings from './GoogleIntegrationSettings';
import { ConnectorsList } from './ConnectorsPanel';
import { HowTo, GUIDES } from './HowTo';

type Dot = 'connected' | 'partial' | 'off' | 'error';

interface PublicAddressInfo {
  url: string | null;
  source: 'saved' | 'env' | null;
  local: string;
  googleRedirectUri: string;
  telegramWebhook: string | null;
}

const Card: React.FC<{ id: string; dot: Dot; title: string; summary: string; open: boolean; onToggle: () => void; children: React.ReactNode }> = ({ dot, title, summary, open, onToggle, children }) => (
  <div className={`connection-card ${open ? 'open' : ''}`}>
    <div className="connection-head" onClick={onToggle} role="button" aria-expanded={open}>
      <span className={`status-dot dot-${dot}`} />
      <div className="connection-title">
        <strong>{title}</strong>
        <span className="connection-sub">{summary}</span>
      </div>
      <span className="chev">{open ? '▾' : '▸'}</span>
    </div>
    {open && <div className="connection-body">{children}</div>}
  </div>
);

const KEY_SERVICES = ['openai', 'anthropic', 'replicate', 'google_maps', 'vectorizer_ai', 'home_assistant', 'spotify', 'twitter'];

const ConnectionsPanel: React.FC = () => {
  const { token } = useAuth();
  const google = useGoogleAuth();
  const [creds, setCreds] = useState<Record<string, { hasCredentials: boolean; needsRefresh?: boolean }>>({});
  const [addr, setAddr] = useState<PublicAddressInfo | null>(null);
  const [computers, setComputers] = useState<Array<{ id: string; name: string; connected: boolean; lastSeen: number | null; version?: string }>>([]);
  const [pairing, setPairing] = useState<{ command: string; download: string } | null>(null);
  const [mail, setMail] = useState<{ configured: boolean; email: string | null; correspondents: number } | null>(null);
  const [town, setTown] = useState<{ configured: boolean; connected: boolean; name: string | null; town: string; url: string } | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    try {
      const r = await fetch('/api/credentials/status', { headers });
      if (r.ok) {
        const d = await r.json();
        const map: Record<string, { hasCredentials: boolean; needsRefresh?: boolean }> = {};
        for (const s of (d.status || d.services || [])) map[s.service] = { hasCredentials: !!(s.connected || s.hasCredentials), needsRefresh: !!s.needsRefresh };
        setCreds(map);
      }
    } catch { /* the cards fall back to "unknown" */ }
    try {
      const r = await fetch('/api/public-address', { headers });
      if (r.ok) setAddr(await r.json());
    } catch { /* the address card shows "not set" until the server answers */ }
    try {
      const r = await fetch('/api/computer/status', { headers });
      if (r.ok) setComputers((await r.json()).computers || []);
    } catch { /* the computer card shows "not paired" until the server answers */ }
    try {
      const r = await fetch('/api/mail/identity', { headers });
      if (r.ok) setMail(await r.json());
    } catch { /* the email card shows "not set up" until the server answers */ }
    try {
      const r = await fetch('/api/world/status', { headers });
      if (r.ok) setTown(await r.json());
    } catch { /* the town card shows "not set up" until the server answers */ }
  }, [token]);
  useEffect(() => { reload(); const t = setInterval(reload, 30000); return () => clearInterval(t); }, [reload]);

  const toggle = (id: string) => setOpen(o => (o === id ? null : id));

  const hasAddress = !!addr?.url;
  const addressSummary = addr?.url ? `${addr.url}${addr.source === 'env' ? ' · from his environment' : ''}` : 'Not set · reachable from this machine only';

  const telegram = creds['telegram'];
  const telegramDot: Dot = !telegram?.hasCredentials ? 'off' : (hasAddress && !telegram.needsRefresh) ? 'connected' : 'partial';
  const telegramSummary = !telegram?.hasCredentials ? 'Not set up · web chat only' : hasAddress ? 'Bot connected · he can reach you anywhere' : 'Bot on file · two-way once his public address is set';

  const googleDot: Dot = !google.isConfigured ? 'off' : google.isAuthenticated ? 'connected' : 'partial';
  const googleSummary = !google.isConfigured ? 'Not set up' : google.isAuthenticated ? `Connected · ${(google.connectedServices || []).join(', ') || 'mail, calendar'}` : 'Keys on file · sign in to finish';

  const mailKey = !!creds['agentmail']?.hasCredentials;
  const mailDot: Dot = !mailKey ? 'off' : mail?.email ? 'connected' : 'partial';
  const mailSummary = !mailKey ? 'Not set up · he can only send mail through your Gmail' : mail?.email ? `${mail.email}${mail.correspondents ? ` · ${mail.correspondents} correspondent${mail.correspondents === 1 ? '' : 's'}` : ''}` : 'Key on file · creating his inbox';

  const townDot: Dot = !town?.configured ? 'off' : town.connected ? 'connected' : 'partial';
  const townSummary = !town?.configured ? 'Not set up · no cottage yet' : town.connected ? `Connected · lives in ${town.town} as ${town.name}` : 'Token on file · connecting to the town';

  const anyComputer = computers.some(c => c.connected);
  const computerDot: Dot = !computers.length ? 'off' : anyComputer ? 'connected' : 'partial';
  const computerSummary = !computers.length ? 'Not paired · no hands on a PC' : anyComputer ? `Connected · ${computers.filter(c => c.connected).map(c => c.name).join(', ')}` : `Paired (${computers.length}) · EmCog Hands is not running`;
  const pairComputer = async () => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    const r = await fetch('/api/computer/pair', { method: 'POST', headers, body: JSON.stringify({ name: 'Computer' }) });
    if (r.ok) { const d = await r.json(); setPairing({ command: d.command, download: d.download }); reload(); }
  };
  const unpairComputer = async (id: string) => {
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    await fetch(`/api/computer/${encodeURIComponent(id)}`, { method: 'DELETE', headers });
    reload();
  };

  const keysOn = KEY_SERVICES.filter(k => creds[k]?.hasCredentials).length;

  return (
    <div className="tab-content connections-content">
      <h3>🔗 Connections</h3>
      <p className="panel-lede">Everything outside himself, in one place: his address on the internet, how he reaches you, the accounts he acts through, the outside services you've plugged in, and the keys he runs on. Open a card to set it up.</p>

      <div className="connection-section-label">His address</div>
      <Card id="address" dot={hasAddress ? 'connected' : 'off'} title="Public address" summary={addressSummary} open={open === 'address'} onToggle={() => toggle('address')}>
        <HowTo guide={GUIDES.address} defaultOpen={!hasAddress} />
        {addr?.source === 'env' && <p className="connection-note">Set by PUBLIC_URL in his environment. An address saved here takes precedence.</p>}
        {addr && !hasAddress && <p className="connection-note">Right now everything points at <code>{addr.local}</code>: fine on this machine, unreachable from anywhere else.</p>}
        <CredentialSettings only={['public_address']} embedded onChanged={reload} />
      </Card>
      <Card id="computer" dot={computerDot} title="Computer — hands on a PC" summary={computerSummary} open={open === 'computer'} onToggle={() => toggle('computer')}>
        <HowTo guide={GUIDES.computer} defaultOpen={!computers.length} />
        {computers.map(c => (
          <p key={c.id} className="connection-note">{c.connected ? '🟢' : '⚪'} <b>{c.name}</b>{c.version ? ` · EmCog Hands ${c.version}` : ''}{c.lastSeen ? ` · last seen ${new Date(c.lastSeen).toLocaleString()}` : ' · never connected'} <button className="download-btn" onClick={() => unpairComputer(c.id)}>Remove</button></p>
        ))}
        <p><button className="download-btn" onClick={pairComputer}>Pair this computer</button> <a className="download-btn" href="/downloads/emcog-hands.exe">Download EmCog Hands</a></p>
        {pairing && <div className="connection-note">Double-click EmCog Hands and paste this line when it asks. It is shown only now:<pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', userSelect: 'all' }}>{pairing.command}</pre></div>}
      </Card>
      <Card id="mail" dot={mailDot} title="Email — his own address" summary={mailSummary} open={open === 'mail'} onToggle={() => toggle('mail')}>
        <HowTo guide={GUIDES.mail} defaultOpen={!mailKey} />
        {mail?.email && <p className="connection-note">He writes and receives as <code>{mail.email}</code>. A letter that arrives is something that happened to him; he reads it and decides whether to answer.</p>}
        <CredentialSettings only={['agentmail']} embedded onChanged={reload} />
      </Card>

      <Card id="town" dot={townDot} title="The town — his cottage" summary={townSummary} open={open === 'town'} onToggle={() => toggle('town')}>
        <HowTo guide={GUIDES.town} defaultOpen={!town?.configured} />
        {town?.connected && <p className="connection-note">He lives in <b>{town.town}</b> as <b>{town.name}</b>. Neighbors, letters, his garden plot and his house are all there.</p>}
        <CredentialSettings only={['world']} embedded onChanged={() => { reload(); setTimeout(reload, 4000); }} />
      </Card>

      <div className="connection-section-label">Where he reaches you</div>
      <Card id="telegram" dot={telegramDot} title="Telegram" summary={telegramSummary} open={open === 'telegram'} onToggle={() => toggle('telegram')}>
        <HowTo guide={GUIDES.telegram} defaultOpen={!telegram?.hasCredentials} />
        <CredentialSettings only={['telegram']} embedded onChanged={reload} />
      </Card>

      <div className="connection-section-label">Your accounts</div>
      <Card id="google" dot={googleDot} title="Google — mail and calendar" summary={googleSummary} open={open === 'google'} onToggle={() => toggle('google')}>
        <HowTo guide={GUIDES.google} defaultOpen={!google.isConfigured} />
        {addr && <p className="connection-note">Redirect URI to register in Google Cloud Console: <code>{addr.googleRedirectUri}</code></p>}
        {!google.isConfigured && <CredentialSettings only={['google']} embedded onChanged={reload} />}
        <GoogleIntegrationSettings embedded />
      </Card>

      <div className="connection-section-label">Outside services (MCP)</div>
      <HowTo guide={GUIDES.connectors} />
      <ConnectorsList embedded />

      <div className="connection-section-label" style={{ marginTop: 18 }}>Services he runs on · API keys ({keysOn} of {KEY_SERVICES.length} on file)</div>
      <HowTo guide={GUIDES.services} />
      <CredentialSettings only={KEY_SERVICES} embedded onChanged={reload} />
    </div>
  );
};

export default ConnectionsPanel;
